import '../styles.css';
import { IPF_NETWORK_VIEWER, narrowType } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { GraphBreadcrumbContainer } from 'components/Graph/GraphBreadcrumbs/GraphBreadcrumbContainer';
import type { GraphBreadcrumbProps } from 'components/Graph/GraphBreadcrumbs/GraphBreadcrumbProps';
import type { NetworkPathBreadcrumb } from 'components/Graph/GraphBreadcrumbs/helpers/buildBreadcrumbs';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { GraphPathWarning } from 'components/Graph/GraphPathWarning/GraphPathWarning';
import { isTopologyGraphMetadata } from 'components/Graph/GraphState';
import { fitGraphToView } from 'components/Graph/commands/fitGraphToView';
import { highlightBox } from 'components/Graph/commands/highlightBox';
import { loadGraph } from 'components/Graph/commands/loadGraph';
import { showSidePanel } from 'components/Graph/commands/showSidePanel';
import { unhideNodesInPath } from 'components/Graph/commands/unhideNodesInPath';
import { unhighlightBox } from 'components/Graph/commands/unhighlightBox';
import { getBoxIdByPath } from 'components/Graph/helpers/getBoxIdByPath';
import { selectIsolateStpi } from 'components/Graph/model/selectors/graphMetadata/selectIsolateStpi';
import { flow } from 'fp-ts/lib/function';
import type { FC, MouseEvent } from 'react';

export const getNetworkPathBreadcrumbText = (
  breadcrumb: NetworkPathBreadcrumb,
): string => breadcrumb.label ?? breadcrumb.path;

export const NetworkPathBreadcrumbItem: FC<
  GraphBreadcrumbProps<NetworkPathBreadcrumb>
> = (props) => {
  const ctxProps = useGraphContextSelectors({
    boxLabels: prop('graph', 'boxLabels'),
    dispatch: prop('dispatch'),
    isolateStpi: selectIsolateStpi,
    topologyMetadata: flow(
      prop('graphMetadata'),
      narrowType(isTopologyGraphMetadata),
    ),
  });

  const boxId = getBoxIdByPath(props.content.path);

  const handleEmptyWarningClick = (e: MouseEvent) => {
    e.stopPropagation();
    ctxProps.dispatch(showSidePanel, {
      topologyLayoutSettingsTabId: 'devices',
    });
  };

  const handleHiddenWarningClick = (e: MouseEvent) => {
    e.stopPropagation();
    ctxProps.dispatch(unhideNodesInPath, { path: props.content.path });
  };

  const renderEmptyWarning = () => {
    if (ctxProps.isolateStpi) {
      return <GraphPathWarning type='emptyStpiIsolate' />;
    }
    if (
      props.content.path === IPF_NETWORK_VIEWER &&
      ctxProps.topologyMetadata.allPathsSelected
    ) {
      // ignore case when network viewer path has no nodes because all paths are expanded
      return null;
    }
    return <GraphPathWarning type='empty' onClick={handleEmptyWarningClick} />;
  };

  return (
    <GraphBreadcrumbContainer
      containerStyle='default'
      onClick={() => {
        if (boxId) {
          ctxProps.dispatch(fitGraphToView, { boxId });
        }
      }}
      onMouseEnter={() => {
        if (boxId) {
          ctxProps.dispatch(highlightBox, { boxId });
        }
      }}
      onMouseLeave={() => {
        if (boxId) {
          ctxProps.dispatch(unhighlightBox, { boxId });
        }
      }}
      onRemoveButtonClick={() => {
        const paths = ctxProps.topologyMetadata.parameters.paths.filter(
          (p) => p !== boxId,
        );
        ctxProps.dispatch(loadGraph, {
          overlay: 'keep',
          parameters: {
            ...ctxProps.topologyMetadata.parameters,
            paths,
          },
        });

        if (paths.length === 0) {
          ctxProps.dispatch(showSidePanel, { networkViewerTabId: 'dataInput' });
        }
      }}
      removalIndicated={props.removalIndicated}
    >
      {getNetworkPathBreadcrumbText(props.content)}
      {props.content.missing && <GraphPathWarning type='missing' />}
      {props.content.empty && renderEmptyWarning()}
      {props.content.hidden && (
        <GraphPathWarning type='hidden' onClick={handleHiddenWarningClick} />
      )}
    </GraphBreadcrumbContainer>
  );
};
